import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { MonthlyGoalReport } from './storeSalesGoalsService';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: { finalY: number };
  }
}

export class PDFReportService {
  private static instance: PDFReportService;

  static getInstance(): PDFReportService {
    if (!PDFReportService.instance) {
      PDFReportService.instance = new PDFReportService();
    }
    return PDFReportService.instance;
  }

  private formatMonthYear(monthYear: string): string {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  async generateStoreGoalReportPDF(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): Promise<Blob> {
    try {
      console.log('📄 PDFReportService: Starting PDF generation...');
      console.log('Report data:', report);
      
      const doc = new jsPDF();
      const monthName = this.formatMonthYear(monthYear);
      console.log('📅 Month name:', monthName);
    
    // Set up colors
    const primaryColor = [31, 41, 55]; // gray-800
    const accentColor = [59, 130, 246]; // blue-500
    const successColor = [34, 197, 94]; // green-500
    const warningColor = [245, 158, 11]; // amber-500
    const dangerColor = [239, 68, 68]; // red-500

    // Header
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 40, 'F');
    
    // Company Logo/Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Chorost & Co.', 20, 20);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text('Store Sales Goals Report', 20, 30);

    // Report Date and Period
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.text(`Report Period: ${monthName}`, 20, 50);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 20, 58);

    // Overall Performance Summary
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.text('Overall Performance Summary', 20, 75);

    // Summary boxes
    const summaryY = 85;
    const boxWidth = 55;
    const boxHeight = 25;
    
    // Total Goal box
    doc.setFillColor(240, 249, 255); // blue-50
    doc.rect(20, summaryY, boxWidth, boxHeight, 'F');
    doc.setDrawColor(...accentColor);
    doc.rect(20, summaryY, boxWidth, boxHeight);
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Goal', 22, summaryY + 8);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(this.formatCurrency(report.totalGoalAmount), 22, summaryY + 18);

    // Total Sales box
    doc.setFillColor(240, 253, 244); // green-50
    doc.rect(80, summaryY, boxWidth, boxHeight, 'F');
    doc.setDrawColor(...successColor);
    doc.rect(80, summaryY, boxWidth, boxHeight);
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Sales', 82, summaryY + 8);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(this.formatCurrency(report.totalActualSales), 82, summaryY + 18);

    // Completion Percentage box
    const completionColor = report.overallCompletionPercentage >= 100 ? successColor :
                           report.overallCompletionPercentage >= 80 ? warningColor : dangerColor;
    
    doc.setFillColor(254, 249, 195); // yellow-50
    doc.rect(140, summaryY, boxWidth, boxHeight, 'F');
    doc.setDrawColor(...completionColor);
    doc.rect(140, summaryY, boxWidth, boxHeight);
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Completion', 142, summaryY + 8);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...completionColor);
    doc.text(`${report.overallCompletionPercentage.toFixed(1)}%`, 142, summaryY + 18);

    // Store Performance Table
    doc.setTextColor(...accentColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Store Performance Details', 20, 125);

    // Sort stores by completion percentage (highest first)
    const sortedGoals = [...report.goals].sort((a, b) => b.completionPercentage - a.completionPercentage);

    // Prepare table data
    const tableData = sortedGoals.map((goal, index) => {
      const rank = index + 1;
      const emoji = goal.completionPercentage >= 100 ? '🎯' :
                   goal.completionPercentage >= 80 ? '📈' :
                   goal.completionPercentage >= 60 ? '📊' : '📉';
      
      return [
        `${rank}. ${emoji}`,
        goal.storeName,
        this.formatCurrency(goal.goalAmount),
        this.formatCurrency(goal.actualSales),
        `${goal.completionPercentage.toFixed(1)}%`
      ];
    });

    // Create the table
    doc.autoTable({
      startY: 135,
      head: [['Rank', 'Store', 'Goal', 'Actual Sales', 'Completion']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: accentColor,
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10,
        textColor: primaryColor
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20 },
        1: { halign: 'left', cellWidth: 50 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'right', cellWidth: 35 },
        4: { halign: 'center', cellWidth: 30 }
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251] // gray-50
      },
      didParseCell: (data) => {
        // Color code completion percentages
        if (data.column.index === 4 && data.section === 'body') {
          const percentage = parseFloat(data.cell.text[0].replace('%', ''));
          if (percentage >= 100) {
            data.cell.styles.textColor = successColor;
            data.cell.styles.fontStyle = 'bold';
          } else if (percentage >= 80) {
            data.cell.styles.textColor = warningColor;
            data.cell.styles.fontStyle = 'bold';
          } else if (percentage < 60) {
            data.cell.styles.textColor = dangerColor;
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFillColor(...primaryColor);
    doc.rect(0, pageHeight - 20, 210, 20, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Chorost & Co. - Store Center Dashboard', 20, pageHeight - 10);
    doc.text(`Page 1 of 1`, 170, pageHeight - 10);

    // Performance insights (if space allows)
    const finalY = (doc as any).lastAutoTable?.finalY || 200;
    if (finalY < pageHeight - 60) {
      doc.setTextColor(...accentColor);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Insights', 20, finalY + 20);

      doc.setTextColor(...primaryColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      const topPerformer = sortedGoals[0];
      const needsImprovement = sortedGoals.filter(g => g.completionPercentage < 80);
      const achievedGoals = sortedGoals.filter(g => g.completionPercentage >= 100);

      let insightY = finalY + 30;
      
      if (topPerformer) {
        doc.text(`🏆 Top Performer: ${topPerformer.storeName} (${topPerformer.completionPercentage.toFixed(1)}%)`, 20, insightY);
        insightY += 8;
      }
      
      doc.text(`✅ Stores that achieved goals: ${achievedGoals.length} of ${sortedGoals.length}`, 20, insightY);
      insightY += 8;
      
      if (needsImprovement.length > 0) {
        doc.text(`📈 Stores needing improvement: ${needsImprovement.length}`, 20, insightY);
      }
    }

      console.log('✅ PDF generation completed successfully');
      return doc.output('blob');
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        report: report,
        monthYear: monthYear
      });
      throw new Error(`PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async downloadStoreGoalReport(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): Promise<void> {
    try {
      const pdfBlob = await this.generateStoreGoalReportPDF(report, monthYear);
      const monthName = this.formatMonthYear(monthYear);
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Store_Goals_Report_${monthName.replace(' ', '_')}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log(`PDF report downloaded: Store_Goals_Report_${monthName.replace(' ', '_')}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF report:', error);
      throw error;
    }
  }
}

export const pdfReportService = PDFReportService.getInstance();
